import { Close, DragIndicator, Inventory2 } from "@mui/icons-material";
import {
  Autocomplete,
  Avatar,
  Box,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { ColorsConstants } from "../../../cssUtils/constants";

const ProductDisplay = ({
  product,
  catalogId,
  defaultOptions,
  searchProducts,
  selectedRetailerIds,
  onRemoveProduct,
  onChangeProduct,
  dragHandleProps,
}) => {
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chosenOption, setChosenOption] = useState(null);
  const latestRequest = useRef(0);

  // Show the editor's prefetched list until the user types a search term
  const hasSearch = Boolean(search.trim());
  const options = hasSearch ? searchResults : defaultOptions || [];

  // The selected product may not be in the current options, so it is built
  // from what the section stores ({title, retailerId}) rather than looked up
  // in the options.
  const selectedOption = product?.retailerId
    ? options.find((o) => o.retailer_id === product.retailerId) ||
      (chosenOption?.retailer_id === product.retailerId ? chosenOption : null) || {
        name: product.title,
        retailer_id: product.retailerId,
      }
    : null;

  useEffect(() => {
    // Invalidate any in-flight search whenever the term changes or is cleared
    const requestId = ++latestRequest.current;

    if (!search.trim() || !catalogId) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);

    const timer = setTimeout(async () => {
      try {
        const data = await searchProducts(search);
        if (requestId === latestRequest.current) setSearchResults(data);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        if (requestId === latestRequest.current) setLoading(false);
      }
    }, 700);

    return () => clearTimeout(timer);
  }, [search, catalogId]);

  return (
    <Stack sx={{ width: "100%" }} direction="row" alignItems="center" gap={1.5}>
      <Box
        {...dragHandleProps}
        sx={{
          display: "flex",
          alignItems: "center",
          color: ColorsConstants.primaryGray40,
          cursor: "grab",
          "&:active": { cursor: "grabbing" },
        }}
      >
        <DragIndicator />
      </Box>
      <Avatar
        variant="rounded"
        src={selectedOption?.image_url}
        sx={{
          width: 32,
          height: 32,
          bgcolor: ColorsConstants.primaryPurple90,
        }}
      >
        <Inventory2 fontSize="small" />
      </Avatar>
      <Autocomplete
        fullWidth
        options={options}
        value={selectedOption}
        onClose={() => setSearch("")}
        onChange={(e, value) => {
          setChosenOption(value);
          onChangeProduct(value);
          setSearch("");
        }}
        onInputChange={(e, newValue, reason) => {
          if (reason === "input") setSearch(newValue);
        }}
        filterOptions={(opts) => opts}
        loading={loading}
        loadingText="Searching products..."
        getOptionLabel={(option) => option?.name || ""}
        getOptionDisabled={(option) =>
          option?.retailer_id !== product?.retailerId &&
          (selectedRetailerIds || []).includes(option?.retailer_id)
        }
        renderOption={(props, option) => (
          <Stack
            {...props}
            key={option?.retailer_id}
            direction="column"
            gap={0.5}
            sx={{
              alignItems: "flex-start !important",
              textAlign: "left",
              width: "100%",
            }}
          >
            <Typography>{option?.name}</Typography>
            <Typography variant="body2" color={ColorsConstants.primaryGray40}>
              Retailer ID: {option?.retailer_id}
            </Typography>
          </Stack>
        )}
        isOptionEqualToValue={(option, value) =>
          option?.retailer_id === value?.retailer_id
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Product"
            size="medium"
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        sx={{
          "& .MuiOutlinedInput-root": {
            fontSize: 14,
          },
        }}
        noOptionsText="No products found"
      />
      <Close
        sx={{
          color: ColorsConstants.primaryGray40,
          cursor: "pointer",
          fontSize: 22,
          flexShrink: 0,
        }}
        onClick={onRemoveProduct}
      />
    </Stack>
  );
};

export default ProductDisplay;
